import { ImageStyle, StyleSheet, TextStyle, ViewStyle } from 'react-native';
import { useBreakpoint } from './useBreakpoint';
import { useMemo } from 'react';

type Style = ViewStyle | TextStyle | ImageStyle;
type BreakpointStyle = {
  sm?: Style;
  md?: Style;
  lg?: Style;
  xl?: Style;
}

export type StyleWithBreakpoints = Style & Partial<BreakpointStyle>;
type BreakpointStyles<T> = { [P in keyof T]: StyleWithBreakpoints };

/**
 * A hook that applies responsive styles based on breakpoints.
 * @param styles - An object containing styles with optional breakpoint variations
 * @returns An object with the same shape as the input, but with styles resolved according to current breakpoint
 *
 * @example
 * ```tsx
 * const styles = useBreakpointStyles({
 *   container: {
 *     padding: 10,
 *     sm: { padding: 20 },
 *     md: { padding: 30 },
 *     lg: { padding: 40 }
 *   }
 * });
 * ```
 */
export const useBreakpointStyles = <T extends BreakpointStyles<T>>(styles: T): T => {
  const bp = useBreakpoint();

  // Create the final styles by merging breakpoints
  const finalStyles = useMemo(() => Object.entries<StyleWithBreakpoints>(styles).reduce((acc, [key, style]) => {
    const baseStyle = { ...style };

    // Remove breakpoint keys from base style
    delete baseStyle.sm;
    delete baseStyle.md;
    delete baseStyle.lg;
    delete baseStyle.xl;

    // Apply breakpoint styles based on window width
    let breakpointStyle = {};
    if (bp.xl && style.xl) {
      breakpointStyle = style.xl;
    } else if (bp.lg && style.lg) {
      breakpointStyle = style.lg;
    } else if (bp.md && style.md) {
      breakpointStyle = style.md;
    } else if (style.sm) {
      breakpointStyle = style.sm;
    }

    return {
      ...acc,
      [key]: StyleSheet.flatten([baseStyle, breakpointStyle])
    };
  }, {} as T), [bp.lg, bp.md, bp.xl, styles]);

  return finalStyles;
};
